import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, Inject, OnDestroy, OnInit, signal } from '@angular/core';
import { environment } from '../environments/environment';
import { DashboardComponent } from './dashboard/dashboard.component';
import { LoginComponent } from './login/login.component';

import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { CandidatesService } from './services/candidates.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, LoginComponent, DashboardComponent],
  templateUrl: './app.html',
  styleUrl: './app.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class App implements OnInit, OnDestroy {
  title = 'YourExpats - Login';
  isIframe = false;
  loginDisplay = false;
  environment = environment;
  private readonly _destroying$ = new Subject<void>();
  private candidatesFetched = false;

  userName = signal<string>('');
  userEmail = signal<string>('');
  userFirstName = signal<string>('');
  userLastName = signal<string>('');
  userRoles = signal<string[]>([]);

  authLoaded = false;

  canOnboard = computed(() => this.userRoles().includes('login.onboard'));
  canSelect = computed(() => this.userRoles().includes('login.select'));
  canFind = computed(() => this.userRoles().includes('login.find'));
  canTest = computed(() => this.userRoles().includes('login.test'));

  onboardUrl = environment.onboardUrl;
  selectUrl = environment.selectUrl;
  findUrl = environment.findUrl;
  testUrl = environment.testUrl;

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private cdr: ChangeDetectorRef,
    private candidatesService: CandidatesService
  ) { }

  private fetchCandidatesOnce() {
    if (this.candidatesFetched) return;
    this.candidatesService.getCandidates().subscribe({
      next: (data) => console.log('candidates:', data),
      error: (err) => console.error('candidates error:', err)
    });
    this.candidatesFetched = true;
  }

  ngOnInit(): void {
    this.isIframe = window !== window.parent && !window.opener;

    this.authService.handleRedirectObservable()
      .pipe(takeUntil(this._destroying$))
      .subscribe((result) => {
        if (result) {

          this.fetchCandidatesOnce();
        }
      });

    this.setLoginDisplay();

    this.authService.instance.enableAccountStorageEvents();

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        } else {
          this.setLoginDisplay();
          this.cdr.markForCheck();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
        this.authLoaded = true;
        this.cdr.markForCheck();
      });
  }

  setLoginDisplay() {
    this.loginDisplay = !!this.authService.instance.getActiveAccount();
    const activeAccount = this.authService.instance.getActiveAccount();

    if (activeAccount) {
      this.userName.set(activeAccount.name || '');
      this.userEmail.set(activeAccount.username || '');
      const idTokenClaims: any = activeAccount.idTokenClaims;
      this.userRoles.set(idTokenClaims?.roles || []);

      if (idTokenClaims?.given_name) {
        this.userFirstName.set(idTokenClaims.given_name);
      } else if (activeAccount.name) {
        this.userFirstName.set(activeAccount.name.split(' ')[0]);
      }

      if (idTokenClaims?.family_name) {
        this.userLastName.set(idTokenClaims.family_name);
      } else if (activeAccount.name) {
        const parts = activeAccount.name.split(' ');
        if (parts.length > 1) {
          this.userLastName.set(parts.slice(1).join(' '));
        }
      }

      if (!this.candidatesFetched) {
        this.candidatesService.getCandidates().subscribe({
          next: (data) => console.log('candidates:', data),
          error: (err) => console.error('candidates error:', err)
        });
        this.candidatesFetched = true;
      }
    }
  }

  checkAndSetActiveAccount() {

    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      const accounts = this.authService.instance.getAllAccounts();
      if (accounts.length === 1) {
        this.authService.instance.setActiveAccount(accounts[0]);
        activeAccount = accounts[0];
      } else {

        const authRequest = this.msalGuardConfig.authRequest ? { ...this.msalGuardConfig.authRequest } : { scopes: [] };
        this.authService.loginRedirect({
          ...authRequest,
          prompt: 'select_account'
        } as RedirectRequest);
        return;
      }
    }

    if (activeAccount) {
      this.setLoginDisplay();
    }
  }

  login() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest, prompt: 'select_account' } as RedirectRequest);
    } else {
      this.authService.loginRedirect({ prompt: 'select_account' } as RedirectRequest);
    }
  }

  logout() {
    this.candidatesFetched = false;
    this.authService.logout({
      postLogoutRedirectUri: environment.msalConfig.auth.postLogoutRedirectUri
    });
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
